import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { ProjectGovernanceState, type ProjectGovernanceDto } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export function CommunicationPlanForm({
  projectId,
  governance,
}: {
  projectId: string;
  governance: ProjectGovernanceDto;
}) {
  const { t } = useTranslation();
  const canEdit =
    governance.state !== ProjectGovernanceState.Closed &&
    (governance.permissions.isBusinessLayer ||
      governance.permissions.isProjectManager ||
      governance.permissions.isExecutive);
  const schema = yup
    .object({
      alertResponseDays: yup.string().required(),
      highlightReportWeekday: yup.string().required(),
      gatePrepWorkingDays: yup.string().required(),
      staleUpdateWorkingDays: yup.string().required(),
      pausedAlertDays: yup.string().required(),
    })
    .required();
  const { register, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    values: {
      alertResponseDays: String(governance.alertResponseDays),
      highlightReportWeekday: String(governance.highlightReportWeekday),
      gatePrepWorkingDays: String(governance.gatePrepWorkingDays),
      staleUpdateWorkingDays: String(governance.staleUpdateWorkingDays),
      pausedAlertDays: String(governance.pausedAlertDays),
    },
  });
  const mutation = useGovernanceMutation(projectId, (v: { alertResponseDays: string; highlightReportWeekday: string; gatePrepWorkingDays: string; staleUpdateWorkingDays: string; pausedAlertDays: string }) =>
    projectGovernanceApi.setCommunicationPlan(projectId, {
      expectedVersion: governance.version,
      alertResponseDays: Number(v.alertResponseDays),
      alertBackupUserId: governance.alertBackupUserId,
      highlightReportWeekday: Number(v.highlightReportWeekday),
      gatePrepWorkingDays: Number(v.gatePrepWorkingDays),
      staleUpdateWorkingDays: Number(v.staleUpdateWorkingDays),
      pausedAlertDays: Number(v.pausedAlertDays),
    }),
  );

  if (!canEdit) {
    return (
      <p className="text-sm">
        <span className="text-muted-foreground">{t('workspace.governance.communicationPlan.title')}: </span>
        {t('workspace.governance.communicationPlan.summary', {
          days: governance.alertResponseDays,
          weekday: t(`workspace.governance.communicationPlan.weekday.${governance.highlightReportWeekday}`),
          gate: governance.gatePrepWorkingDays,
          stale: governance.staleUpdateWorkingDays,
          paused: governance.pausedAlertDays,
        })}
      </p>
    );
  }

  return (
    <form className="space-y-2" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
      <h5 className="text-sm font-medium">{t('workspace.governance.communicationPlan.title')}</h5>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label htmlFor="alertResponseDays">{t('workspace.governance.communicationPlan.alertResponseDays')}</Label>
          <Input id="alertResponseDays" type="number" min={1} max={30} {...register('alertResponseDays')} />
        </div>
        <div>
          <Label htmlFor="highlightReportWeekday">{t('workspace.governance.communicationPlan.highlightWeekday')}</Label>
          <select
            id="highlightReportWeekday"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            {...register('highlightReportWeekday')}
          >
            {WEEKDAYS.map((d) => (
              <option key={d} value={d}>
                {t(`workspace.governance.communicationPlan.weekday.${d}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="gatePrepWorkingDays">{t('workspace.governance.communicationPlan.gatePrepWorkingDays')}</Label>
          <Input id="gatePrepWorkingDays" type="number" min={1} max={30} {...register('gatePrepWorkingDays')} />
        </div>
        <div>
          <Label htmlFor="staleUpdateWorkingDays">{t('workspace.governance.communicationPlan.staleUpdateWorkingDays')}</Label>
          <Input id="staleUpdateWorkingDays" type="number" min={1} max={30} {...register('staleUpdateWorkingDays')} />
        </div>
        <div>
          <Label htmlFor="pausedAlertDays">{t('workspace.governance.communicationPlan.pausedAlertDays')}</Label>
          <Input id="pausedAlertDays" type="number" min={1} max={30} {...register('pausedAlertDays')} />
        </div>
      </div>
      <Button type="submit" size="sm" disabled={mutation.isPending}>
        {t('workspace.governance.communicationPlan.save')}
      </Button>
    </form>
  );
}
